import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["S", "M", "T", "W", "T", "F", "S"];
const fmtDate = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
const fmtTime = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return `${((h + 11) % 12) + 1}:${pad(m)} ${h >= 12 ? "PM" : "AM"}`; };

export default function SessionDatePicker({ sessions, onPick }) {
  const byDate = useMemo(() => sessions.reduce((a, s) => { (a[s.date] ||= []).push(s); return a; }, {}), [sessions]);
  const isOpen = (d) => (byDate[d] || []).some((s) => !s.sold_out && s.spots_remaining > 0);
  const firstOpen = sessions.find((s) => !s.sold_out && s.spots_remaining > 0)?.date || sessions[0]?.date;
  const [fy, fm] = (firstOpen || new Date().toISOString().slice(0, 10)).split("-").map(Number);
  const [view, setView] = useState({ y: fy, m: fm - 1 });
  const [picked, setPicked] = useState(null);

  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1).getDay();
    const days = new Date(view.y, view.m + 1, 0).getDate();
    const arr = Array(first).fill(null);
    for (let d = 1; d <= days; d++) arr.push(iso(view.y, view.m, d));
    while (arr.length % 7) arr.push(null);
    return arr;
  }, [view]);
  const nav = (dir) => setView((v) => { const m = v.m + dir; return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 }; });
  const times = picked ? byDate[picked] || [] : [];

  return (
    <div data-testid="session-date-picker">
      <div className="bg-white rounded-3xl shadow-card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={() => nav(-1)} aria-label="Previous month" data-testid="picker-prev-month" className="p-2 rounded-full hover:bg-cream-alt"><ChevronLeft size={18}/></button>
          <div className="font-serif text-xl" data-testid="picker-month-label">{MONTHS[view.m]} {view.y}</div>
          <button type="button" onClick={() => nav(1)} aria-label="Next month" data-testid="picker-next-month" className="p-2 rounded-full hover:bg-cream-alt"><ChevronRight size={18}/></button>
        </div>
        <div className="grid grid-cols-7 text-center text-xs font-bold text-charcoal-muted mb-1">{DOW.map((d, i) => <div key={i}>{d}</div>)}</div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((d, i) => {
            if (!d) return <div key={`e-${i}`}/>;
            const open = isOpen(d);
            const has = !!byDate[d];
            const sel = picked === d;
            return (
              <button type="button" key={d} disabled={!open} onClick={() => setPicked(d)} data-testid={`picker-day-${d}`} aria-pressed={sel}
                className={`aspect-square rounded-xl text-sm font-semibold flex flex-col items-center justify-center transition-colors
                  ${sel ? "bg-terracotta text-white" : open ? "bg-forest/15 text-forest hover:bg-forest/25" : has ? "bg-cream-alt text-charcoal-muted line-through" : "text-charcoal/35"}`}>
                {Number(d.slice(-2))}
                {open && !sel && <span className="w-1.5 h-1.5 rounded-full bg-forest mt-0.5"/>}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex gap-4 text-[11px] text-charcoal-muted">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-forest/15 inline-block"/> Available</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-cream-alt inline-block"/> Sold out</span>
        </div>
      </div>

      {picked && (
        <div className="mt-5" data-testid="picker-times">
          <h2 className="font-serif text-xl">{fmtDate(picked)}</h2>
          <div className="mt-3 grid gap-3">
            {times.map((s) => {
              const off = s.sold_out || s.spots_remaining === 0;
              return (
                <button key={s.id} type="button" disabled={off} onClick={() => onPick(s)} data-testid={`pick-session-${s.id}`}
                  className={`flex items-center justify-between rounded-2xl px-5 py-4 text-left shadow-card border-2 ${off ? "bg-cream-alt text-charcoal-muted border-transparent cursor-not-allowed" : "bg-white border-transparent hover:border-terracotta"}`}>
                  <span className="inline-flex items-center gap-2 font-semibold"><Clock size={16}/> {fmtTime(s.start_time)}</span>
                  <span className="text-sm font-bold">{off ? "Sold Out" : "Available"}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {!picked && <p className="mt-4 text-sm text-charcoal-muted text-center" data-testid="picker-hint">Tap a highlighted date to see times.</p>}
    </div>
  );
}
